import React, {useState, useEffect, useCallback, useMemo} from 'react';
import { Button, Typography, Box, Autocomplete, CircularProgress, Alert, TextField } from '@mui/material';
import axios from 'axios';
import { API_BASE_URL, getContests } from '../api/contests';
import DynamicFormFields, { FormField } from './DynamicFormFields';
import {debounce } from 'lodash';

const fakeUser = {
  id: 22,
  first_name: "Some",
  last_name: "One",
  username: "someone",
  language_code: "en",
  is_premium: true,
  allows_write_to_pm: true
};

const tg = (window as any)?.Telegram?.WebApp;
const telegramUser = tg?.initDataUnsafe?.user ?? fakeUser;

interface Contest {
  contest_id: number;
  name: string;
}

const evidenceUrlField: FormField = {
  field_name: 'evidence_url',
  display_label: 'Введите ссылку на видео',
  data_type: 'text',
  required: false,
};

const TelegramAddContestEntry: React.FC = () => {
  const [contests, setContests] = useState<Contest[]>([]);
  const [formFields, setFormFields] = useState<FormField[]>([]);
  const [formData, setFormData] = useState<{ [key: string]: any }>({});
  const [participantData, setParticipantData] = useState('');
  const [evidenceUrl, setEvidenceUrl] = useState('');
  const [mainResultField, setMainResultField] = useState<string | null>(null);
  const [resultCalcRule, setResultCalcRule] = useState<any>(null);
  const [ranking, setRanking] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [loadingContests, setLoadingContests] = useState(true);
  const [loadingForm, setLoadingForm] = useState(false);
  const [selectedContest, setSelectedContest] = useState<Contest | null>(null);

  const participantName = telegramUser.username || `${telegramUser.first_name} ${telegramUser.last_name || ''}`;
  const contestId = selectedContest?.contest_id;

  useEffect(() => {
    getContests()
      .then((data) => setContests(data))
      .catch((error) => console.error('Error fetching contests:', error))
      .finally(() => setLoadingContests(false));
  }, []);

  useEffect(() => {
    if (contestId) {
      setLoadingForm(true);
      axios
        .get(`${API_BASE_URL}/contests/${contestId}`)
        .then((response) => {
          const metadata = response.data?.form_metadata;
          const parsedMetadata = typeof metadata === 'string' ? JSON.parse(metadata) : metadata;
          const fields = parsedMetadata.fields?.filter((f: any) => !f.non_entry_field) || [];
          if (fields?.length > 0) fields.push(evidenceUrlField);

          setFormFields(fields);
          setMainResultField(fields.find((f: FormField) => f.main_result_field)?.field_name || null);
          setResultCalcRule(parsedMetadata.result_calc || null);
          setFormData({});
          setRanking(null);
        })
        .catch((error) => console.error('Error fetching form metadata:', error))
        .finally(() => setLoadingForm(false));
    }
  }, [contestId]);

  const handleInputChange = (name: string, value: any) => {
    if (name === 'participant_data') {
      setParticipantData(value);
    } else if (name === 'evidence_url') {
      setEvidenceUrl(value);
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const calculateResult = (rule: any, data: { [key: string]: any }): number | null => {
    if (!rule || !rule.fields) return null;
    const { op, fields } = rule;
    const values: number[] = fields.map((field: any) => parseFloat(data[field]) || 0);

    switch (op) {
      case 'sum': return values.reduce((acc, val) => acc + val, 0);
      case 'diff': return values.reduce((acc, val) => acc - val);
      case 'mult': return values.reduce((acc, val) => acc * val, 1);
      case 'div': return values.reduce((acc, val) => (val !== 0 ? acc / val : acc));
      default: return null;
    }
  };

  // Convert time from 'hh:mm:ss' format to 'hhmmss'
  const convertTimeToHHMMSS = (time: string): string => {
    const timeParts = time.split(':');
    if (timeParts.length === 3) {
      return timeParts.join('');
    }
    return time;
  };


  const debounceCheckRanking = useCallback(
    debounce((contestId, grouping, requiredResult) => {
      // Check if the result is in 'hh:mm:ss' format and convert it
      const formattedResult = requiredResult.toString().includes(':')
        ? convertTimeToHHMMSS(requiredResult)
        : requiredResult;
  
      axios
        .get(`${API_BASE_URL}/telegram/new-ranking`, {
          params: { contest_id: contestId, grouping, new_result: formattedResult },
        })
        .then((response) => setRanking(response.data.ranking))
        .catch(() => setRanking(null));
    }, 300),
    []
  );
  

  useEffect(() => {
    const requiredResult = resultCalcRule
      ? calculateResult(resultCalcRule, formData)
      : formData[mainResultField || ''];
  
    if (contestId && formData.grouping && requiredResult !== undefined) {
      // Convert the result if it's in 'hh:mm:ss' format
      const formattedResult = typeof requiredResult === 'string' && requiredResult.includes(':')
        ? convertTimeToHHMMSS(requiredResult)
        : requiredResult;
  
      if (!isNaN(Number(formattedResult))) {
        debounceCheckRanking(contestId, formData.grouping, formattedResult);
      }
    }
  }, [contestId, formData, resultCalcRule, mainResultField, debounceCheckRanking]);
  

  const allRequiredFieldsFilled = formFields.every((field) => !field.required || formData[field.field_name]);
  const shouldDisableSubmit = !allRequiredFieldsFilled  || !evidenceUrl;

  const handleSubmit = () => {
    setIsSubmitting(true);
    axios
      .post(`${API_BASE_URL}/telegram/submit-result`, {
        participant_name: participantName,
        first_name: telegramUser.first_name,
        last_name: telegramUser.last_name,
        username: telegramUser.username,
        tg_id: telegramUser.id,
        is_premium: telegramUser.is_premium,
        contest_name: selectedContest?.name,
        contest_id: contestId,
        entry_data: {...formData, participant_data: participantData, evidence_url: evidenceUrl},
      })
      .then(() => setSubmissionSuccess(true))
      .catch(() => setSubmissionError('Произошла ошибка. Попробуйте ещё раз или сообщите администратору.'))
      .finally(() => {
        setHasSubmitted(true);
        setIsSubmitting(false);
      });
  };

  return (
    <Box p={3}>
      <Typography variant="h5" gutterBottom>Приветствую, {participantName}!</Typography>
      {submissionSuccess && <Alert severity="success" sx={{ mb: 2 }}>✅ Ваш результат отправлен!</Alert>}
      {submissionError && <Alert severity="error" sx={{ mb: 2 }}>⚠️ {submissionError}</Alert>}

      {loadingContests ? (
        <Box display="flex" justifyContent="center" mt={2}><CircularProgress /></Box>
      ) : (
        <Autocomplete
          options={contests}
          getOptionLabel={(option) => option.name}
          onChange={(event, value) => setSelectedContest(value)}
          renderInput={(params) => <TextField {...params} label="Выберите зарубу" variant="outlined" />}
          fullWidth
          style={{ marginBottom: '16px', marginTop: 20 }}
        />
      )}

      {selectedContest && (
        <>
          {loadingForm ? (
            <Box display="flex" justifyContent="center" mt={2}><CircularProgress /></Box>
          ) : (
            <DynamicFormFields formFields={formFields} formData={{ ...formData, participant_data: participantData, evidence_url: evidenceUrl }} onInputChange={handleInputChange} contestId={contestId?.toString()} />
          )}

          <TextField color="primary"
            label="Предварительный рейтинг"
            value={ranking ?? '—'}
            InputProps={{ readOnly: true }}
            fullWidth
            margin="normal"
            variant="outlined"
          />
          {ranking === 1 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              ⚠️ Ваш результат претендует на 1-е место, желаю удачи!
            </Alert>
          )
          }
          {submissionSuccess && <Alert severity="success" sx={{ mb: 2 }}>✅ Ваш результат отправлен!</Alert>}
          {submissionError && <Alert severity="error" sx={{ mb: 2 }}>⚠️ {submissionError}</Alert>}
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={shouldDisableSubmit || isSubmitting || hasSubmitted }
          >
            Послать результат
          </Button>
        </>
      )}
    </Box>
  );
};

export default TelegramAddContestEntry;
